import { Box } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CheckboxGroupField from 'components/forms/fields/checkbox-group-field/checkbox-group-field.component';
import DaysMultipleSelectField from 'components/forms/fields/days-multiple-select-field/days-multiple-select-field.component';
import { useKitchenCapacityContext } from 'components/kitchen-capacity/kitchen-capacity.context';
import { filterPreparationTimeOverlappedEvents } from 'components/kitchen-capacity/preparation-time-calendar/preparation-time.utils';
import ValueConditionField from './fields/value-condition-field/value-condition-field.component';
import { useEffect } from 'react';
import TimeRangePickerField from 'components/forms/fields/time-range-picker-field/time-range-picker-field.component';

type SetPreparationTimeFormProps = {
  eventId: string;
};
const SetPreparationTimeForm = ({ eventId }: SetPreparationTimeFormProps) => {
  const { t } = useTranslation();
  const {
    trigger,
    formState: { isSubmitted },
  } = useFormContext();
  const orderType = useWatch({ name: 'orderType' });
  const daysOfWeek = useWatch({ name: 'daysOfWeek' });
  const timeRange = useWatch({ name: 'timeRange' });
  const throttleSettings = useWatch({ name: 'throttleSettings' });
  const {
    preparationTime: { preparationTimeEvents },
  } = useKitchenCapacityContext();

  const orderTypeOptions = [
    {
      label: t('forms.setPreparationTimeForm.fields.orderType.options.pickup'),
      id: 'PICKUP',
    },
    {
      label: t(
        'forms.setPreparationTimeForm.fields.orderType.options.delivery',
      ),
      id: 'DELIVERY',
    },
  ];

  useEffect(() => {
    if (isSubmitted) trigger('throttleSettings');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [throttleSettings]);

  const hasOverlap =
    filterPreparationTimeOverlappedEvents(
      { eventId, orderType, daysOfWeek, timeRange, throttleSettings },
      preparationTimeEvents,
    ).length > 0;

  return (
    <Box className="field-container" display="grid" gap={3}>
      <CheckboxGroupField
        name="orderType"
        options={orderTypeOptions}
        FormGroupProps={{
          sx: {
            flexDirection: 'row',
          },
        }}
      />

      <DaysMultipleSelectField
        name="daysOfWeek"
        label={t('forms.setPreparationTimeForm.fields.daysOfWeek.label')}
        data-testid="SetPreparationTimeForm.DaysOfWeek.Field"
        TextFieldProps={{ margin: 'none' }}
      />

      <TimeRangePickerField
        name="timeRange"
        helperText={
          hasOverlap &&
          t('forms.setPreparationTimeForm.fields.timeRange.warnings.overlap')
        }
        helperTextSeverity="warning"
        minutesStep={15}
        fromLabel={t('forms.setPreparationTimeForm.fields.timeRange.fromLabel')}
        toLabel={t('forms.setPreparationTimeForm.fields.timeRange.toLabel')}
        data-testid="SetPreparationTimeForm.TimeRange.Field"
      />

      <ValueConditionField />
    </Box>
  );
};

export default SetPreparationTimeForm;
